import type { CallToAction } from "pages/media/types";
import { normalizeActionUrl, hasLabelAndValue } from "pages/media/utils";
import type { ExperienceLink, ExperienceLinkType } from "../types";

/** Header-link type → backend `ActionType`. */
const LINK_TYPE_TO_ACTION_TYPE: Record<ExperienceLinkType, string> = {
  link: "ExternalLink",
  email: "Email",
  calendar: "CalendarAppointment",
  phone: "Phone",
};

/** The reverse of the map above. A `CallToAction` created outside this card
 *  (e.g. `DocumentDownload`, from the classic `LinksCtaSection` editor) has
 *  no matching card type, so it's filtered out rather than guessed at. */
const ACTION_TYPE_TO_LINK_TYPE: Partial<Record<string, ExperienceLinkType>> =
  Object.fromEntries(
    Object.entries(LINK_TYPE_TO_ACTION_TYPE).map(([link, action]) => [
      action,
      link as ExperienceLinkType,
    ])
  );

/**
 * Whether a link has both a label and a value. A link missing either can't
 * be sent to the backend, so it's also the signal for "nothing real to lose"
 * (e.g. gating the edit card's remove confirmation) and for what actually
 * ended up saved.
 */
export const hasLinkContent = (link: ExperienceLink): boolean =>
  hasLabelAndValue(link.label, link.value);

/** The same transform `mapLinksToCallToActions` applies to a value before
 *  saving — exposed so the form can normalize on commit (blur, type change)
 *  instead of only on save, keeping the displayed value and the saved one
 *  from ever disagreeing (MED-7109). */
export const normalizeLinkValue = (
  type: ExperienceLinkType,
  value: string
): string => normalizeActionUrl(LINK_TYPE_TO_ACTION_TYPE[type], value);

/**
 * Maps the card's `ExperienceLink`s to backend `CallToAction`s. A link
 * missing a label or a value is dropped rather than sent half-empty. There's
 * no `enabled` flag on the backend shape — a link either exists in the array
 * or it doesn't, so the card has no separate on/off state either (see
 * `ExperienceLinkItem`). A link carrying over its `id` (untouched since it
 * was fetched) keeps it — only a link added in this session gets a fresh one.
 */
export const mapLinksToCallToActions = (
  links: ExperienceLink[]
): CallToAction[] =>
  links
    .filter(hasLinkContent)
    .map((link) => {
      const actionType = LINK_TYPE_TO_ACTION_TYPE[link.type];
      return {
        id: link.id ?? crypto.randomUUID(),
        ui_definition: { type: "Button", labels: { en: link.label.trim() } },
        action: {
          type: actionType,
          url: normalizeActionUrl(actionType, link.value),
        },
      };
    });

/** Maps a live experience's `CallToAction`s back to the card's shape — used
 *  by the edit card to pre-populate whatever links already exist. */
export const mapCallToActionsToLinks = (
  actions: CallToAction[]
): ExperienceLink[] =>
  actions.reduce<ExperienceLink[]>((links, cta) => {
    const type = ACTION_TYPE_TO_LINK_TYPE[cta.action.type];
    if (!type) return links;
    const label = Object.values(cta.ui_definition.labels)[0] ?? "";
    links.push({ id: cta.id, type, label, value: cta.action.url ?? "" });
    return links;
  }, []);

/** CTAs `mapCallToActionsToLinks` drops (e.g. `DocumentDownload`, created by
 *  the classic `LinksCtaSection` editor) — kept verbatim and merged back into
 *  the save payload so this card's full-replace PATCH doesn't delete them. */
export const getUnmanagedCallToActions = (
  actions: CallToAction[]
): CallToAction[] =>
  actions.filter((cta) => !ACTION_TYPE_TO_LINK_TYPE[cta.action.type]);
